import { useEffect, useState } from "react"

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material'

import { handleApiError, ordersService, ORDER_STATUSES } from "../../api"

import type { Order, OrderStatus } from "../../api/types"

type EditLabOrderModalProps = {
  open: boolean
  onClose: () => void
  order: Order
  onUpdate: (updatedOrder: Order) => void
}

export function EditLabOrderModal({ open, onClose, order, onUpdate }: EditLabOrderModalProps) {
  const [packageId, setPackageId] = useState(order.packageId || "")
  const [status, setStatus] = useState<OrderStatus>(order.status)
  const [junctionOrderId, setJunctionOrderId] = useState(order.junctionOrderId || "")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    setPackageId(order.packageId || "")
    setStatus(order.status)
    setJunctionOrderId(order.junctionOrderId || "")
    setError("")
  }, [order])

  const handleClose = () => {
    if (!isLoading) {
      setPackageId(order.packageId || "")
      setStatus(order.status)
      setJunctionOrderId(order.junctionOrderId || "")
      setError("")
      onClose()
    }
  }

  const handleSave = async () => {
    try {
      setIsLoading(true)
      setError("")

      const updateData: {
        packageId?: string;
        status?: OrderStatus;
        junctionOrderId?: string;
      } = {}

      const trimmedPackageId = packageId.trim()
      if (trimmedPackageId !== (order.packageId || "")) {
        if (trimmedPackageId) {
          updateData.packageId = trimmedPackageId
        }
      }

      if (status !== order.status) {
        updateData.status = status
      }

      const trimmedJunctionOrderId = junctionOrderId.trim()
      if (trimmedJunctionOrderId !== (order.junctionOrderId || "")) {
        if (trimmedJunctionOrderId) {
          updateData.junctionOrderId = trimmedJunctionOrderId
        }
      }

      const updatedOrder = await ordersService.updateOrder(order.id, updateData)

      onUpdate(updatedOrder)
      handleClose()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const hasChanges =
    packageId.trim() !== (order.packageId || "") ||
    status !== order.status ||
    junctionOrderId.trim() !== (order.junctionOrderId || "")

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Edit Lab Order
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Edit details for order: <code>{order.id}</code>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Current Status: <strong>{order.status}</strong> |
            Current Junction ID: <code>{order.junctionOrderId || 'Not set'}</code>
          </Typography>

          <TextField
            autoFocus
            fullWidth
            label="Package ID"
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
            placeholder="e.g. Basic_Plan_Quest, Complete_Plan_Quest..."
            disabled={isLoading}
            helperText="Enter a valid package ID (e.g. Basic_Plan_Quest, Complete_Plan_Quest, Total_Clarity_Plan_Quest, or their BioReference equivalents)"
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
              }
            }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as OrderStatus)}
              disabled={isLoading}
            >
              {ORDER_STATUSES.map((statusOption) => (
                <MenuItem key={statusOption} value={statusOption}>
                  {statusOption.charAt(0).toUpperCase() + statusOption.slice(1).replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Junction Order ID"
            value={junctionOrderId}
            onChange={(e) => setJunctionOrderId(e.target.value)}
            placeholder="e.g. jnc_123456789"
            disabled={isLoading}
            helperText="Enter the Junction order ID (if available)"
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
              }
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !hasChanges}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}